import { Enums, imageRetrievalPoolManager, utilities, } from '@cornerstonejs/core';
import { Enums as csCoreEnums } from '@cornerstonejs/core';
import { logging } from '@cornerstonejs/metadata';
import createImage from '../createImage.js';
import getPixelData from './getPixelData.js';
import { loadImageFromCompressedFrameRegistry } from './loadImageFromRegistry.js';
import { DEFAULT_MS_BETWEEN_DECODE } from '../internal/retrieveDefaults.js';
const log = logging.loaderLog.getLogger('wadors');
const { ProgressiveIterator } = utilities;
const { ImageQualityStatus } = Enums;
const streamableTransferSyntaxes = new Set([
    '3.2.840.10008.1.2.4.96',
    '1.2.840.10008.1.2.4.202',
    '1.2.840.10008.1.2.4.203',
]);
export function getTransferSyntaxForContentType(contentType) {
    const defaultTransferSyntax = '1.2.840.10008.1.2';
    if (!contentType) {
        return defaultTransferSyntax;
    }
    const parameters = contentType.split(';');
    const params = {};
    parameters.forEach((parameter) => {
        const parameterValues = parameter.split('=');
        if (parameterValues.length !== 2) {
            return;
        }
        const value = parameterValues[1].trim().replace(/"/g, '');
        params[parameterValues[0].trim()] = value;
    });
    const defaultTransferSyntaxByType = {
        'image/jpeg': '1.2.840.10008.1.2.4.50',
        'image/x-dicom-rle': '1.2.840.10008.1.2.5',
        'image/x-jls': '1.2.840.10008.1.2.4.80',
        'image/jls': '1.2.840.10008.1.2.4.80',
        'image/jll': '1.2.840.10008.1.2.4.70',
        'image/jp2': '1.2.840.10008.1.2.4.90',
        'image/jpx': '1.2.840.10008.1.2.4.92',
        'image/jxl': '1.2.840.10008.1.2.4.110',
        'application/x-deflate': '1.2.840.10008.1.2.8.1',
        'image/jphc': '3.2.840.10008.1.2.4.96',
    };
    if (params['transfer-syntax']) {
        return params['transfer-syntax'];
    }
    else if (contentType &&
        !Object.keys(params).length &&
        defaultTransferSyntaxByType[contentType]) {
        return defaultTransferSyntaxByType[contentType];
    }
    else if (params.type && defaultTransferSyntaxByType[params.type]) {
        return defaultTransferSyntaxByType[params.type];
    }
    else if (defaultTransferSyntaxByType[contentType]) {
        return defaultTransferSyntaxByType[contentType];
    }
    return defaultTransferSyntax;
}
function getImageRetrievalPool() {
    return imageRetrievalPoolManager;
}
const mediaType = 'multipart/related; type=application/octet-stream; transfer-syntax=*';
function loadImage(imageId, options = {}) {
    const registryPromise = loadImageFromCompressedFrameRegistry(imageId, options);
    if (registryPromise) {
        return {
            promise: registryPromise.catch((error) => {
                log.warn(`Failed to decode registry frame data for ${imageId}; falling back to network retrieval`, error);
                return loadImageFromNetwork(imageId, options).promise;
            }),
            cancelFn: undefined,
        };
    }
    return loadImageFromNetwork(imageId, options);
}
function loadImageFromNetwork(imageId, options = {}) {
    const imageRetrievalPool = getImageRetrievalPool();
    const start = new Date().getTime();
    const uncompressedIterator = new ProgressiveIterator('decompress');
    async function sendXHR(imageURI, imageId, mediaType) {
        uncompressedIterator.generate(async (it) => {
            const compressedIt = ProgressiveIterator.as(getPixelData(imageURI, imageId, mediaType, options));
            let lastDecodeLevel = 10;
            let lastDecodeTime = 0;
            for await (const result of compressedIt) {
                const { pixelData, imageQualityStatus = ImageQualityStatus.FULL_RESOLUTION, percentComplete, done = true, extractDone = true, } = result;
                const transferSyntax = getTransferSyntaxForContentType(result.contentType);
                if (!extractDone && !streamableTransferSyntaxes.has(transferSyntax)) {
                    continue;
                }
                const retrieveDecodeLevel = options.retrieveOptions?.decodeLevel;
                const decodeLevel = result.decodeLevel ??
                    (retrieveDecodeLevel === 0 ||
                        imageQualityStatus === ImageQualityStatus.FULL_RESOLUTION
                        ? 0
                        : decodeLevelFromComplete(percentComplete, retrieveDecodeLevel));
                if (!shouldDecodeAgain({
                    done,
                    decodeLevel,
                    lastDecodeLevel,
                    lastDecodeTime,
                    now: Date.now(),
                    msBetweenDecode: options.retrieveOptions?.msBetweenDecode ??
                        DEFAULT_MS_BETWEEN_DECODE,
                })) {
                    continue;
                }
                try {
                    const useOptions = {
                        ...options,
                        decodeLevel,
                    };
                    const image = (await createImage(imageId, pixelData, transferSyntax, useOptions));
                    const end = new Date().getTime();
                    image.loadTimeInMS = end - start;
                    image.transferSyntaxUID = transferSyntax;
                    image.imageQualityStatus = imageQualityStatus;
                    it.add(image, done);
                    lastDecodeLevel = decodeLevel;
                    lastDecodeTime = Date.now();
                }
                catch (e) {
                    if (extractDone) {
                        log.warn("Couldn't decode", e);
                        throw e;
                    }
                }
            }
        });
    }
    const requestType = options.requestType || csCoreEnums.RequestType.Interaction;
    const additionalDetails = options.additionalDetails || { imageId };
    const priority = options.priority === undefined ? 5 : options.priority;
    const uri = imageId.substring(7);
    imageRetrievalPool.addRequest(sendXHR.bind(this, uri, imageId, mediaType), requestType, additionalDetails, priority);
    return {
        promise: uncompressedIterator.getDonePromise(),
        cancelFn: undefined,
    };
}
export function shouldDecodeAgain({ done, decodeLevel, lastDecodeLevel, lastDecodeTime, now, msBetweenDecode, }) {
    if (done) {
        return true;
    }
    if (decodeLevel > 0) {
        return decodeLevel < lastDecodeLevel;
    }
    if (decodeLevel < lastDecodeLevel) {
        return true;
    }
    return now - lastDecodeTime >= msBetweenDecode;
}
function decodeLevelFromComplete(percent, retrieveDecodeLevel = 4) {
    const testSize = percent / 100 - 0.02;
    if (testSize > 1 / 4) {
        return Math.min(retrieveDecodeLevel, 0);
    }
    if (testSize > 1 / 16) {
        return Math.min(retrieveDecodeLevel, 1);
    }
    if (testSize > 1 / 64) {
        return Math.min(retrieveDecodeLevel, 2);
    }
    return Math.min(retrieveDecodeLevel, 3);
}
export default loadImage;
